import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { InitializeUserRequest, initiliazeUser } from '../clients/userClient';

interface BackendUser {
  name: string;
  email: string;
  tier: string;
}

type UserContextValue = BackendUser;

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  const name = user?.user_metadata?.display_name ?? '';
  const email = user?.email ?? '';

  const { data: backendUser } = useQuery({
    queryKey: ['backendUser', user?.email],
    queryFn: () => {
      const payload: InitializeUserRequest = { name, email };
      console.log('Running initiliazeUser', payload);
      return initiliazeUser(payload); // should upsert and return the row
    },
    enabled: !!user, // do nothing until we have a user
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0 // avoid automatic retries that spam the server
  });

  // If you want to fall back to auth metadata before backend responds:
  const value = useMemo<UserContextValue | undefined>(() => {
    if (!user) return undefined;
    if (backendUser) return backendUser;
    return { name, email, tier: 'Free' };
  }, [backendUser, user, name, email]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (ctx === undefined) throw new Error('useUserContext must be used within a UserProvider');
  return ctx;
};
